import { QUALIFICATION_CODE, SurveyQualification } from '@ps/survey-utils';

/**
 * Which qualifications feasibility targets on, and the guarantee that an age
 * band is always among them.
 *
 * Generic over the criterion it produces because the same selection feeds two
 * unrelated shapes — the display model the drawer labels read, and the request
 * DTO the wire carries. Subclasses supply only the projection.
 */
export abstract class FeasibilityCriteriaMapperBase<TCriterion> {
  constructor(private readonly _qualifications: SurveyQualification[]) {}

  public map(): TCriterion[] {
    const targeted = this._qualifications.filter((qualification) =>
      this._isTargeted(qualification),
    );

    const criteria = targeted.map((qualification) =>
      this.toCriterion(qualification),
    );

    // Read off the qualifications rather than the mapped criteria: the criterion
    // shape is the subclass's business, so this stays output-agnostic.
    const hasAgeCriterion = targeted.some(
      (qualification) =>
        qualification.qualificationCode === QUALIFICATION_CODE.AGE,
    );

    return hasAgeCriterion
      ? criteria
      : [...criteria, this.defaultAgeCriterion()];
  }

  protected abstract toCriterion(
    qualification: SurveyQualification,
  ): TCriterion;

  protected abstract defaultAgeCriterion(): TCriterion;

  /**
   * Narrowed by the buyer, or quota'd. Both signals are needed: `isDefault`
   * carries the backend's "was narrowed" for a loaded survey, while a
   * qualification quota'd during creation keeps `isDefault` true and is only
   * distinguishable by `isQuotaSet`.
   */
  private _isTargeted(qualification: SurveyQualification): boolean {
    return !qualification.isDefault || qualification.isQuotaSet;
  }
}
